import { ColumnType } from "antd/es/table";
import React, { useEffect, useState, useMemo } from "react";
import { useDispatch } from "react-redux";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router-dom";
import { PhasesListViewProps } from "../ProjectScheduleManagement/phases/PhasesListView";
import { TooltipComponent } from "../../components/Component";
import { Drawer, Table, Progress, Tooltip } from "antd";
import dayjs from "dayjs";
import reportApis from "../../api/reports";
import { tasksApis } from "../../api/NewTasks";

interface TaskItem {
  taskId: string;
  taskName: string;
  taskOwnerName: string;
  taskPlannedStartDate: string;
  taskPlannedEndDate: string;
  taskActualStartDate: string;
  taskActualEndDate: string;
  priorityName: string;
  completionPercentage: number;
  taskStatus: string;
  uid: string;
  overdueStatus: string;
}

const UserWiseWorkloadReport = () => {
  const [OwnerFilterData, setOwnerFilterData] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [overdueDrawerVisible, setOverdueDrawerVisible] = useState(false);
  const [tasksData, setTasksData] = useState<TaskItem[]>([]);
  const [overdueTasksData, setOverdueTasksData] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{
    userId: string;
    userName: string;
  } | null>(null);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { id: projectId, projectName } = location.state || {};

  const taskColumns: ColumnType<TaskItem>[] = useMemo(
    () => [
      {
        title: "Task Name",
        dataIndex: "taskName",
        key: "taskName",
        width: 250,
        fixed: "left",
        render(text: any, record: any) {
          return (
            <>
              <span
                className="inlineTableCellLink"
                id={`task-${record?.taskId}`}
                onClick={() => {
                  navigate(`/pms/project/task-detail/${record.taskId}`, {
                    state: {
                      projectId,
                      projectName,
                      returnPath: location.pathname,
                      returnState: location.state,
                    },
                  });
                }}
              >
                {text}
              </span>
            </>
          );
        },
      },
      {
        title: "Owner",
        dataIndex: "taskOwnerName",
        key: "taskOwnerName",
        width: 250,
      },
      {
        title: "Start Date",
        dataIndex: "taskPlannedStartDate",
        key: "taskPlannedStartDate",
        width: 200,
        render: (text: string) => text || "N/A",
      },
      {
        title: "End Date",
        dataIndex: "taskPlannedEndDate",
        key: "taskPlannedEndDate",
        width: 200,
        render: (text: string) => text || "N/A",
      },
      {
        title: "Priority",
        dataIndex: "priorityName",
        key: "priorityName",
        width: 150,
      },
      {
        title: "Progress",
        dataIndex: "completionPercentage",
        key: "completionPercentage",
        width: 200,
        render: (data: any) => {
          return (
            <Tooltip title={`${data}% Complete`} placement="top">
              <Progress
                strokeLinecap="butt"
                size={[186, 10]}
                percent={data}
                status="active"
                strokeColor={{
                  from: "#87d068",
                  to: "#87d068",
                }}
              />
            </Tooltip>
          );
        },
      },
      {
        title: "Status",
        dataIndex: "taskStatus",
        key: "taskStatus",
        width: 150,
        fixed: "right",
      },
    ],
    [dispatch]
  );

  const overdueTaskColumns: ColumnType<TaskItem>[] = useMemo(
    () => [
      {
        title: "Task Name",
        dataIndex: "taskName",
        key: "taskName",
        width: 200,
        fixed: "left",
        render(text: any, record: any) {
          return (
            <>
              <span
                className="inlineTableCellLink"
                id={`task-${record?.taskId}`}
                onClick={() => {
                  navigate(`/pms/project/task-detail/${record.taskId}`, {
                    state: {
                      projectId,
                      projectName,
                      returnPath: location.pathname,
                      returnState: location.state,
                    },
                  });
                }}
              >
                {text}
              </span>
            </>
          );
        },
      },
      {
        title: "Planned Start Date",
        dataIndex: "plannedStartDate",
        key: "plannedStartDate",
        width: 150,
        render: (text: string) => text || "N/A",
      },
      {
        title: "Planned End Date",
        dataIndex: "plannedEndDate",
        key: "plannedEndDate",
        width: 150,
        render: (text: string) => text || "N/A",
      },
      {
        title: "Actual Start Date",
        dataIndex: "actualStartDate",
        key: "actualStartDate",
        width: 150,
        render: (text: string) => text || "N/A",
      },
      {
        title: "Actual End Date",
        dataIndex: "taskActualEndDate",
        key: "taskActualEndDate",
        width: 150,
        render: (text: string) => text || "N/A",
      },
      {
        title: "Owner",
        dataIndex: "userName",
        key: "userName",
        width: 150,
      },
      {
        title: "Overdue Status",
        dataIndex: "overDueStatus",
        key: "overDueStatus",
        width: 150,
        render: (text: string) => text || "N/A",
      },
      {
        title: "Task Status",
        dataIndex: "taskStatus",
        key: "taskStatus",
        width: 150,
      },
      {
        title: "Completion %",
        dataIndex: "completedPercentage",
        key: "completedPercentage",
        width: 120,
        render: (text: number) => `${text}%` || "N/A",
      },
    ],
    [dispatch]
  );

  const handleTotalTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.assigedId,
      userName: record.assigedTo,
    });
    setLoading(true);
    setDrawerVisible(true);

    try {
      const response = await tasksApis.listTasks({
        page: 1,
        pageSize: 0,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "ASC",
        direction: "P",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.assigedId,
      userName: record.assigedTo,
    });
    setLoading(true);
    setDrawerVisible(true);

    try {
      const response = await tasksApis.listTasks({
        page: 1,
        pageSize: 0,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "ASC",
        direction: "P",
        taskStatus: "Open",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleInProgressTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.assigedId,
      userName: record.assigedTo,
    });
    setLoading(true);
    setDrawerVisible(true);

    try {
      const response = await tasksApis.listTasks({
        page: 1,
        pageSize: 0,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "ASC",
        direction: "P",
        taskStatus: "InProgress",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnderQCTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.assigedId,
      userName: record.assigedTo,
    });
    setLoading(true);
    setDrawerVisible(true);

    try {
      const response = await tasksApis.listTasks({
        page: 1,
        pageSize: 0,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "ASC",
        direction: "P",
        taskStatus: "UnderQC",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompletedTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.assigedId,
      userName: record.assigedTo,
    });
    setLoading(true);
    setDrawerVisible(true);

    try {
      const response = await tasksApis.listTasks({
        page: 1,
        pageSize: 0,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "ASC",
        direction: "P",
        taskStatus: "Closed",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setTasksData(data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOverdueTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.assigedId,
      userName: record.assigedTo,
    });
    setLoading(true);
    setOverdueDrawerVisible(true);

    try {
      const response = await reportApis.fetchPMSReports({
        page: 1,
        pageSize: 10,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "DESC",
        direction: "P",
        reportName: "overDueCountListForUserWiseWorkLoad",
        taskStatus: "Overdue",
        taskOwnerIds: record.assigedId,
      });

      const { status, data } = response || {};
      if (status) {
        setOverdueTasksData(data?.list || []);
      }
    } catch (error) {
      console.error("Error fetching overdue tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerFilterData = async () => {
    try {
      const response = await tasksApis.getOwnersFilterData(
        projectId?.toString()
      );
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.userName,
            value: item.userId,
            userId: item.userId,
          };
        });
        setOwnerFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    fetchOwnerFilterData();
    dispatch(
      updateMenuData([
        {
          text: "User-Wise Workload Report",
          link: "pms/userwise-workload-report",
        },
      ])
    );
  }, []);

  const columns: ColumnType<any>[] = [
    {
      title: "User Name",
      dataIndex: "assigedTo",
      key: "assigedTo",
      width: 100,
      render: (text) => (text && text.trim() !== "" ? text : "N/A"),
    },
    {
      title: "Role Name",
      dataIndex: "taskUserRole",
      key: "taskUserRole",
      width: 100,
      render: (text) => (text && text.trim() !== "" ? text : "N/A"),
    },
    {
      title: "Reporting To",
      dataIndex: "reportingToName",
      key: "reportingToName",
      width: 150,
      render: (text) => (text && text.trim() !== "" ? text : "N/A"),
    },
    {
      title: "Planned Hours",
      dataIndex: "plannedHours",
      key: "plannedHours",
      width: 120,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Logged Hours",
      dataIndex: "loggedHours",
      key: "loggedHours",
      width: 120,
      render: (text) =>
        text === null || text === undefined || text === "" ? "N/A" : text,
    },
    {
      title: "Total Tasks",
      dataIndex: "totalTasks",
      key: "totalTasks",
      width: 100,
      render(text: any, record: TaskItem) {
        return (
          <>
            <div
              className="inlineTableCellLink"
              id={`task-${record?.taskId}`}
              onClick={() => handleTotalTasksClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`task-${record?.taskId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Open Tasks",
      dataIndex: "totalOpenTasks",
      key: "totalOpenTasks",
      width: 100,
      render(text: any, record: TaskItem) {
        return (
          <>
            <div
              className="inlineTableCellLink"
              id={`task-${record?.taskId}`}
              onClick={() => handleOpenTasksClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`task-${record?.taskId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>InProgress Tasks</span>,
      dataIndex: "totalInProgressTasks",
      key: "totalInProgressTasks",
      width: 150,
      render(text: any, record: TaskItem) {
        return (
          <>
            <div
              className="inlineTableCellLink"
              id={`task-${record?.taskId}`}
              onClick={() => handleInProgressTasksClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`task-${record?.taskId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Under Quality Check</span>,
      dataIndex: "totalUnderQCTasks",
      key: "totalUnderQCTasks",
      width: 180,
      render(text: any, record: TaskItem) {
        return (
          <>
            <div
              className="inlineTableCellLink"
              id={`task-${record?.taskId}`}
              onClick={() => handleUnderQCTasksClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`task-${record?.taskId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Completed Tasks</span>,
      dataIndex: "totalCompletedTasks",
      key: "totalCompletedTasks",
      width: 150,
      render(text: any, record: PhasesListViewProps) {
        return (
          <>
            <div
              className="inlineTableCellLink"
              id={`phase-${record?.phaseId}`}
              onClick={() => handleCompletedTasksClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`phase-${record?.phaseId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: <span style={{ whiteSpace: "nowrap" }}>Overdue Tasks</span>,
      dataIndex: "totalOverdueTasks",
      key: "totalOverdueTasks",
      width: 130,
      render(text: any, record: PhasesListViewProps) {
        return (
          <>
            <div
              className="inlineTableCellLink"
              id={`phase-${record?.phaseId}`}
              onClick={() => handleOverdueTasksClick(record)}
            >
              {text}
            </div>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`phase-${record?.phaseId}`}
              direction="right"
              text={"Click To View " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: (
        <span style={{ whiteSpace: "nowrap" }}>Completion Percentage</span>
      ),
      dataIndex: "completedPercentage",
      key: "completedPercentage",
      width: 200,
      render: (text, record) => {
        if (
          text === null ||
          text === undefined ||
          text === "" ||
          text === "N/A"
        )
          return "N/A";

        const percentage = parseFloat(text.toString().replace("%", ""));

        return (
          <Tooltip title={`Completion: ${text}`} placement="top">
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percentage}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      },
    },
  ];

  return (
    <>
      <Template1
        moduleKey={window.location.pathname}
        columns={columns}
        pageTitle="UserWise Workload Report"
        pageDescription=""
        previousPath={{
          link: "/pms/project/reports",
          state: location.state,
        }}
        filters={{
          defaultFilters: {
            dateRange: {
              title: "Custom Date Range",
            },
          },
          customFilters: [
            {
              type: "multiple_selection",
              name: "UserId",
              label: "Users",
              options: OwnerFilterData,
            },
          ],
        }}
      />

      <Drawer
        title={`Tasks for ${selectedUser?.userName || "User"}`}
        width={1200}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Table
          columns={taskColumns}
          dataSource={tasksData}
          loading={loading}
          scroll={{ x: 1300 }}
          rowKey="taskId"
        />
      </Drawer>

      <Drawer
        title={`Overdue Tasks for ${selectedUser?.userName || "User"}`}
        width={1200}
        onClose={() => setOverdueDrawerVisible(false)}
        open={overdueDrawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Table
          columns={overdueTaskColumns}
          dataSource={overdueTasksData}
          loading={loading}
          scroll={{ x: 1500 }}
          rowKey="taskId"
        />
      </Drawer>
    </>
  );
};

export default UserWiseWorkloadReport;
